import React, { useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useGlobalSearch from "../hooks/useGlobalSearch";
import styles from "./GlobalSearch.module.css";

const CATEGORY_META = {
  navigation: { icon: "\u2192", label: "Go to" },
  officer: { icon: "\uD83D\uDC6E", label: "Officers" },
  job: { icon: "\uD83D\uDCCB", label: "Jobs" },
  entity: { icon: "\uD83C\uDFE2", label: "Organizations" },
  agency: { icon: "\uD83C\uDFDB\uFE0F", label: "Agencies" },
};

const STATUS_COLORS = {
  active: "#16a34a",
  pending: "#d97706",
  completed: "#6b7280",
  cancelled: "#dc2626",
  inactive: "#9ca3af",
  blocked: "#dc2626",
  rejected: "#dc2626",
};

// Highlights matching substrings in text by wrapping them in <mark>
function HighlightText({ text, query }) {
  if (!query || !text) return <>{text}</>;
  const q = query.trim().toLowerCase();
  if (!q) return <>{text}</>;

  const parts: React.ReactNode[] = [];
  let remaining = text;
  let lower = remaining.toLowerCase();
  let idx = lower.indexOf(q);

  while (idx !== -1) {
    if (idx > 0) parts.push(remaining.slice(0, idx));
    parts.push(<mark key={parts.length} className={styles.highlight}>{remaining.slice(idx, idx + q.length)}</mark>);
    remaining = remaining.slice(idx + q.length);
    lower = remaining.toLowerCase();
    idx = lower.indexOf(q);
  }
  if (remaining) parts.push(remaining);
  return <>{parts}</>;
}

function ResultItem({ item, isSelected, onSelect, onNavigate, query, itemRef }) {
  const meta = CATEGORY_META[item.type] || {};
  return (
    <button
      ref={itemRef}
      className={`${styles.resultItem} ${isSelected ? styles.resultItemSelected : ""}`}
      onMouseEnter={onSelect}
      onClick={() => onNavigate(item)}
      type="button"
    >
      <span className={styles.resultIcon}>{meta.icon}</span>
      <div className={styles.resultContent}>
        <span className={styles.resultLabel}>
          <HighlightText text={item.label} query={query} />
        </span>
        {item.sublabel && (
          <span className={styles.resultSublabel}>
            <HighlightText text={item.sublabel} query={query} />
          </span>
        )}
        {item.preview && (
          <span className={styles.resultPreview}>{item.preview}</span>
        )}
      </div>
      {item.status && (
        <span className={styles.resultStatus} style={{ color: STATUS_COLORS[item.status] || "#6b7280" }}>
          {item.status}
        </span>
      )}
      {item.type === "navigation" && <span className={styles.resultArrow}>{"\u21B5"}</span>}
    </button>
  );
}

function CategoryGroup({ label, items, startIndex, selectedIndex, onSelect, onNavigate, query, itemRefs }) {
  if (!items.length) return null;
  return (
    <div className={styles.category}>
      <div className={styles.categoryLabel}>{label}</div>
      {items.map((item, i) => (
        <ResultItem
          key={item.id || item.path || i}
          item={item}
          isSelected={startIndex + i === selectedIndex}
          onSelect={() => onSelect(startIndex + i)}
          onNavigate={onNavigate}
          query={query}
          itemRef={(el) => { if (itemRefs) itemRefs.current[startIndex + i] = el; }}
        />
      ))}
    </div>
  );
}

export default function GlobalSearch({ jobs, users, entities, currentUser, navigationShortcuts = null, extraResults = null, extraCategoryLabel = null, placeholder = null }) {
  const navigate = useNavigate();
  const search = useGlobalSearch({ jobs, users, entities, currentUser, navigationShortcuts, extraResults });
  const { query, setQuery, debouncedQuery, isOpen, open, close, results, flatResults, selectedIndex, setSelectedIndex, handleKeyDown, inputRef, recentSearches, recordSearch, clearRecent } = search;

  // Refs for auto-scrolling selected items into view
  const itemRefs = useRef<Record<number, HTMLElement | null>>({});
  const modalRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Store focus on open, restore on close
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  // Focus trap inside modal
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;
    const modal = modalRef.current;
    const handleTrap = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusable = modal.querySelectorAll<HTMLElement>('button, input, [tabindex]:not([tabindex="-1"])');
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    modal.addEventListener("keydown", handleTrap);
    return () => modal.removeEventListener("keydown", handleTrap);
  }, [isOpen]);

  // Auto-scroll selected item into view
  useEffect(() => {
    const el = itemRefs.current[selectedIndex];
    if (el) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedIndex]);

  const navigateTo = useCallback((item) => {
    recordSearch(query);
    if (item.path) {
      navigate(item.path);
    }
    close();
  }, [navigate, close, query, recordSearch]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (flatResults[selectedIndex]) {
      navigateTo(flatResults[selectedIndex]);
    }
  }, [flatResults, selectedIndex, navigateTo]);

  const handleRecentClick = useCallback((recentQuery) => {
    setQuery(recentQuery);
  }, [setQuery]);

  // Global keyboard shortcut: Ctrl+K or /
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        if (isOpen) { close(); } else { open(); }
      }
      if (e.key === "/" && !isOpen) {
        const tag = document.activeElement?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (document.activeElement as HTMLElement | null)?.isContentEditable) return;
        e.preventDefault();
        open();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, open, close]);

  return (
    <>
      {/* Desktop trigger button */}
      <button className={styles.trigger} onClick={open} type="button" aria-label="Search">
        <svg className={styles.triggerIcon} viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
        </svg>
        <span className={styles.triggerText}>Search...</span>
        <kbd className={styles.triggerKbd}>Ctrl K</kbd>
      </button>

      {/* Mobile trigger icon */}
      <button className={styles.mobileTrigger} onClick={open} type="button" aria-label="Search">
        <svg className={styles.mobileTriggerIcon} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
        </svg>
      </button>

      {/* Modal overlay */}
      {isOpen && (
        <div className={styles.overlay} onClick={close} role="presentation" aria-hidden="true">
          <div
            className={styles.modal}
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <form onSubmit={handleSubmit} className={styles.inputRow}>
              <svg className={styles.searchIcon} viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              <input
                ref={inputRef}
                className={styles.input}
                type="text"
                placeholder={placeholder || "Search officers, jobs, organizations, or type a page name..."}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                autoComplete="off"
                spellCheck={false}
              />
              <button type="button" className={styles.closeBtn} onClick={close} aria-label="Close search">
                <kbd>ESC</kbd>
              </button>
            </form>

            <div className={styles.results}>
              {query.trim() === "" ? (
                <div className={styles.hint}>
                  <p className={styles.hintText}>Type to search across everything</p>
                  <div className={styles.hintExamples}>
                    <span className={styles.hintExample}>Try: officer name, job title, "invoices", "calendar"</span>
                  </div>
                  {recentSearches.length > 0 && (
                    <div className={styles.recentSection}>
                      <div className={styles.recentHeader}>
                        <span className={styles.recentTitle}>Recent</span>
                        <button className={styles.recentClear} onClick={clearRecent} type="button">Clear</button>
                      </div>
                      <div className={styles.recentList}>
                        {recentSearches.map((q) => (
                          <button key={q} className={styles.recentItem} onClick={() => handleRecentClick(q)} type="button">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12" className={styles.recentIcon}>
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : results.total === 0 ? (
                <div className={styles.empty}>
                  <span className={styles.emptyIcon}>&#128269;</span>
                  <span>No results for &ldquo;{query}&rdquo;</span>
                </div>
              ) : (
                <>
                  <CategoryGroup
                    label="Go to"
                    items={results.navigation}
                    startIndex={0}
                    selectedIndex={selectedIndex}
                    onSelect={setSelectedIndex}
                    onNavigate={navigateTo}
                    query={debouncedQuery}
                    itemRefs={itemRefs}
                  />
                  <CategoryGroup
                    label="Officers"
                    items={results.officers}
                    startIndex={results.navigation.length}
                    selectedIndex={selectedIndex}
                    onSelect={setSelectedIndex}
                    onNavigate={navigateTo}
                    query={debouncedQuery}
                    itemRefs={itemRefs}
                  />
                  <CategoryGroup
                    label="Jobs"
                    items={results.jobs}
                    startIndex={results.navigation.length + results.officers.length}
                    selectedIndex={selectedIndex}
                    onSelect={setSelectedIndex}
                    onNavigate={navigateTo}
                    query={debouncedQuery}
                    itemRefs={itemRefs}
                  />
                  <CategoryGroup
                    label="Organizations"
                    items={results.entities}
                    startIndex={results.navigation.length + results.officers.length + results.jobs.length}
                    selectedIndex={selectedIndex}
                    onSelect={setSelectedIndex}
                    onNavigate={navigateTo}
                    query={debouncedQuery}
                    itemRefs={itemRefs}
                  />
                  {results.extra?.length > 0 && (
                    <CategoryGroup
                      label={extraCategoryLabel || "Other"}
                      items={results.extra}
                      startIndex={results.navigation.length + results.officers.length + results.jobs.length + results.entities.length}
                      selectedIndex={selectedIndex}
                      onSelect={setSelectedIndex}
                      onNavigate={navigateTo}
                      query={debouncedQuery}
                      itemRefs={itemRefs}
                    />
                  )}
                  <div className={styles.footer}>
                    <span><kbd className={styles.footerKbd}>{"\u2191"}</kbd><kbd className={styles.footerKbd}>{"\u2193"}</kbd> to navigate</span>
                    <span><kbd className={styles.footerKbd}>{"\u21B5"}</kbd> to select</span>
                    <span><kbd className={styles.footerKbd}>esc</kbd> to close</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
